import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase } from './supabase';
import { Session } from '@supabase/supabase-js';
import { StaffOverview } from './pages/StaffOverview';
import { StaffStudents } from './pages/StaffStudents';
import { StaffSchedule } from './pages/StaffSchedule';
import { StaffAttendance } from './pages/StaffAttendance';
import { StaffRoster } from './pages/StaffRoster';
import { StaffLanding } from './pages/StaffLanding';
import { StaffHistory } from './pages/StaffHistory';

interface StaffUser {
  id: string;
  email: string;
  name: string | null;
  program_ids: string[] | null;
  /**
   * Virtual classes (Chinese / Math) this teacher holds, e.g. `['chinese:3']`.
   *
   * `null` means *none*, deliberately unlike `program_ids` where `null` means every
   * program: a virtual class is an explicit grant. Arrives via the `select('*')` above.
   */
  virtual_class_keys: string[] | null;
  created_at: string;
}

export function StaffApp() {
  const [staffUser, setStaffUser] = useState<StaffUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [unauthorizedAccess, setUnauthorizedAccess] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function checkSession() {
      try {
        setLoading(true);
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) throw sessionError;
        if (!mounted) return;

        setSession(session);

        if (session) {
          const { data, error: staffError } = await supabase
            .from('staff_users')
            .select('*')
            .eq('email', session.user.email)
            .limit(1)
            .single();

          if (staffError) {
            if (staffError.code === 'PGRST116' || staffError.message.includes('No rows returned') || staffError.message.includes('406')) {
              setUnauthorizedAccess(true);
              setLoading(false);
              return;
            }
            throw staffError;
          }

          if (!mounted) return;
          setStaffUser(data);

          // Best-effort: link this auth session to the staff_users row
          supabase.rpc('link_staff_auth_user_id').then(() => {}, () => {});
        }

        setLoading(false);
      } catch (err: unknown) {
        if (!mounted) return;
        const errorObj = err as { status?: number; code?: string; message?: string };
        if (errorObj.status === 406 || errorObj.code === 'PGRST116' || (errorObj.message && errorObj.message.includes('406'))) {
          setUnauthorizedAccess(true);
        } else {
          setError(errorObj.message || 'An unknown error occurred');
        }
        setLoading(false);
      }
    }

    checkSession();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4" />
          <div className="text-xl text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  if (unauthorizedAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-8 bg-white shadow-lg rounded-lg max-w-md">
          <div className="flex justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">Your account does not have teacher access to this area.</p>
          <div className="flex flex-col space-y-3">
            <a
              href="/"
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-center block"
            >
              Return to Home Page
            </a>
            <button
              onClick={() => window.location.reload()}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-4 bg-white shadow rounded-lg">
          <h2 className="text-xl text-red-600 mb-2">Error</h2>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!session || !staffUser) {
    return <Navigate to="/" replace />;
  }

  return (
    <Routes>
      {/* Front door: resolve the teacher's default program and land on its roster. */}
      <Route path="/" element={<StaffLanding staffUser={staffUser} />} />

      {/* Attendance is teacher-only (no parent-facing data) — always available. The
          parent-facing parts of Child Check Mate (authorized pickups, pickup
          notifications) stay flag-gated. */}
      <Route path="/attendance" element={<StaffAttendance staffUser={staffUser} />} />
      <Route path="/attendance/:programId" element={<StaffRoster staffUser={staffUser} />} />

      {/* The archive of what was recorded on the rosters above. */}
      <Route path="/history" element={<StaffHistory staffUser={staffUser} />} />

      {/* NOT CURRENTLY USED — these three pages are deliberately unreachable from the
          navigation, which is attendance-only (see NAV_ITEMS in StaffLayout). The routes
          are kept so existing links and bookmarks still resolve, and so the pages can be
          switched back on by restoring their nav entries. They have not been updated for
          the mobile-first roster work; treat them as dormant, not as current examples. */}
      <Route path="/overview" element={<StaffOverview staffUser={staffUser} />} />
      <Route path="/students" element={<StaffStudents staffUser={staffUser} />} />
      <Route path="/schedule" element={<StaffSchedule staffUser={staffUser} />} />

      <Route path="*" element={<Navigate to="/staff-app" replace />} />
    </Routes>
  );
}
